import type { BoardTarget, MissionConfig } from '../data/missions';
import { MISSION_BY_ID } from '../data/missions';
import {
  boardToDice,
  compareHands,
  compareDiceHands,
  diceSum,
  evaluateHand,
  type HandResult,
} from './handEvaluator';
import { leftOf, rightOf } from './types';

export interface MissionEvalContext {
  playerIndex: number;
  playerCount: number;
  boards: (number | null)[][];
  mission: MissionConfig;
}

function resolveBoardIndex(
  playerIndex: number,
  playerCount: number,
  target: BoardTarget,
): number {
  if (target === 'self') return playerIndex;
  if (target === 'left') return leftOf(playerIndex, playerCount);
  return rightOf(playerIndex, playerCount);
}

function neighborIndex(
  playerIndex: number,
  playerCount: number,
  side: 'left' | 'right',
): number {
  return side === 'left' ? leftOf(playerIndex, playerCount) : rightOf(playerIndex, playerCount);
}

function getHand(board: (number | null)[]): HandResult | null {
  const dice = boardToDice(board);
  if (!dice) return null;
  return evaluateHand(dice);
}

function getDice(board: (number | null)[]): number[] | null {
  return boardToDice(board);
}

function getHandByTarget(ctx: MissionEvalContext, target: BoardTarget): HandResult | null {
  const index = resolveBoardIndex(ctx.playerIndex, ctx.playerCount, target);
  return getHand(ctx.boards[index]);
}

function getDiceByTarget(ctx: MissionEvalContext, target: BoardTarget): number[] | null {
  const index = resolveBoardIndex(ctx.playerIndex, ctx.playerCount, target);
  return getDice(ctx.boards[index]);
}

function resolveTarget(target?: BoardTarget): BoardTarget {
  return target ?? 'self';
}

function allHands(boards: (number | null)[][]): HandResult[] {
  return boards.map((b) => {
    const hand = getHand(b);
    if (!hand) throw new Error('Incomplete board during mission evaluation');
    return hand;
  });
}

function longestConsecutiveRun(dice: number[]): number {
  const unique = [...new Set(dice)].sort((a, b) => a - b);
  if (unique.length === 0) return 0;

  let maxRun = 1;
  let currentRun = 1;

  for (let i = 1; i < unique.length; i += 1) {
    if (unique[i] === unique[i - 1] + 1) {
      currentRun += 1;
    } else {
      maxRun = Math.max(maxRun, currentRun);
      currentRun = 1;
    }
  }

  return Math.max(maxRun, currentRun);
}

function isDiePartOfCombo(dice: number[], dieValue: number, hand: HandResult): boolean {
  const count = dice.filter((d) => d === dieValue).length;

  switch (hand.combo) {
    case 'high':
    case 'straight':
      return true;
    case 'one_pair':
    case 'two_pair':
      return count === 2;
    case 'three_kind':
      return count === 3;
    case 'four_kind':
    case 'five_kind':
      return count >= 4;
    case 'full_house':
      return count >= 2;
    default:
      return false;
  }
}

function hasSameComboName(a: HandResult, b: HandResult): boolean {
  if (a.combo === 'high' || b.combo === 'high') return false;
  return a.combo === b.combo;
}

function hasDifferentComboName(a: HandResult, b: HandResult): boolean {
  if (a.combo === 'high' && b.combo === 'high') return false;
  if (a.combo === 'high' || b.combo === 'high') return true;
  return a.combo !== b.combo;
}

export function evaluateMissionSuccess(ctx: MissionEvalContext): boolean {
  const { playerIndex, playerCount, boards, mission } = ctx;
  const myBoard = boards[playerIndex];
  const myHand = getHand(myBoard);
  const myDice = getDice(myBoard);

  if (!myHand || !myDice) return false;

  const { type, params } = mission;
  const target = resolveTarget(params.target);

  switch (type) {
    case 'min_combo_rank': {
      const hand = getHandByTarget(ctx, target);
      if (!hand) return false;
      return hand.rank <= (params.rank ?? 8);
    }

    case 'exact_combo_rank': {
      const hand = getHandByTarget(ctx, target);
      if (!hand) return false;
      return hand.rank === (params.rank ?? 8);
    }

    case 'exact_combo': {
      const hand = getHandByTarget(ctx, target);
      if (!hand) return false;
      return hand.combo === params.combo;
    }

    case 'sum_min': {
      const dice = getDiceByTarget(ctx, target);
      if (!dice) return false;
      return diceSum(dice) >= (params.count ?? 0);
    }

    case 'sum_max': {
      const dice = getDiceByTarget(ctx, target);
      if (!dice) return false;
      return diceSum(dice) <= (params.count ?? 30);
    }

    case 'value_count_min': {
      const dice = getDiceByTarget(ctx, target);
      if (!dice) return false;
      const value = params.value ?? 0;
      const count = params.count ?? 0;
      return dice.filter((d) => d === value).length >= count;
    }

    case 'parity_count_min': {
      const dice = getDiceByTarget(ctx, target);
      if (!dice) return false;
      const parity = params.parity ?? 'even';
      const count = params.count ?? 0;
      const matches = dice.filter((d) => (parity === 'even' ? d % 2 === 0 : d % 2 === 1));
      return matches.length >= count;
    }

    case 'range_count_min': {
      const dice = getDiceByTarget(ctx, target);
      if (!dice) return false;
      const values = new Set(params.values ?? []);
      const count = params.count ?? 0;
      return dice.filter((d) => values.has(d)).length >= count;
    }

    case 'unique_face_count_min': {
      const dice = getDiceByTarget(ctx, target);
      if (!dice) return false;
      return new Set(dice).size >= (params.count ?? 0);
    }

    case 'contains_all_values': {
      const dice = getDiceByTarget(ctx, target);
      if (!dice) return false;
      const required = params.values ?? [];
      return required.every((value) => dice.includes(value));
    }

    case 'consecutive_count_min': {
      const dice = getDiceByTarget(ctx, target);
      if (!dice) return false;
      return longestConsecutiveRun(dice) >= (params.count ?? 0);
    }

    case 'exact_dice_set': {
      const dice = getDiceByTarget(ctx, target);
      if (!dice) return false;
      const expected = [...(params.dice ?? [])].sort((a, b) => a - b);
      const actual = [...dice].sort((a, b) => a - b);
      return expected.length === 5 && actual.every((value, index) => value === expected[index]);
    }

    case 'compare_hand_higher': {
      const ni = neighborIndex(playerIndex, playerCount, params.neighbor ?? 'right');
      const other = getHand(boards[ni]);
      if (!other) return false;
      return compareHands(myHand, other) > 0;
    }

    case 'compare_hand_lower': {
      const ni = neighborIndex(playerIndex, playerCount, params.neighbor ?? 'right');
      const other = getHand(boards[ni]);
      if (!other) return false;
      return compareHands(myHand, other) < 0;
    }

    case 'compare_targets_hand': {
      const higher = getHandByTarget(ctx, params.higher ?? 'self');
      const lower = getHandByTarget(ctx, params.lower ?? 'right');
      if (!higher || !lower) return false;
      return compareHands(higher, lower) > 0;
    }

    case 'compare_sum_higher': {
      const ni = neighborIndex(playerIndex, playerCount, params.neighbor ?? 'right');
      const otherDice = getDice(boards[ni]);
      if (!otherDice) return false;
      return diceSum(myDice) > diceSum(otherDice);
    }

    case 'compare_sum_lower': {
      const ni = neighborIndex(playerIndex, playerCount, params.neighbor ?? 'right');
      const otherDice = getDice(boards[ni]);
      if (!otherDice) return false;
      return diceSum(myDice) < diceSum(otherDice);
    }

    case 'compare_targets_sum': {
      const higherDice = getDiceByTarget(ctx, params.higher ?? 'self');
      const lowerDice = getDiceByTarget(ctx, params.lower ?? 'right');
      if (!higherDice || !lowerDice) return false;
      return diceSum(higherDice) > diceSum(lowerDice);
    }

    case 'same_combo_name': {
      const ni = neighborIndex(playerIndex, playerCount, params.neighbor ?? 'right');
      const other = getHand(boards[ni]);
      if (!other) return false;
      return hasSameComboName(myHand, other);
    }

    case 'different_combo_name': {
      const ni = neighborIndex(playerIndex, playerCount, params.neighbor ?? 'right');
      const other = getHand(boards[ni]);
      if (!other) return false;
      return hasDifferentComboName(myHand, other);
    }

    case 'same_combo_compare_hand': {
      const higher = getHandByTarget(ctx, params.higher ?? 'self');
      const lower = getHandByTarget(ctx, params.lower ?? 'right');
      if (!higher || !lower) return false;
      if (!hasSameComboName(higher, lower)) return false;
      return compareHands(higher, lower) > 0;
    }

    case 'between_neighbors': {
      const left = getHand(boards[leftOf(playerIndex, playerCount)]);
      const right = getHand(boards[rightOf(playerIndex, playerCount)]);
      if (!left || !right) return false;
      const beatsLeft = compareHands(myHand, left) > 0;
      const losesRight = compareHands(myHand, right) < 0;
      const beatsRight = compareHands(myHand, right) > 0;
      const losesLeft = compareHands(myHand, left) < 0;
      return (beatsLeft && losesRight) || (beatsRight && losesLeft);
    }

    case 'sum_diff_max': {
      const ni = neighborIndex(playerIndex, playerCount, params.neighbor ?? 'right');
      const otherDice = getDice(boards[ni]);
      if (!otherDice) return false;
      return Math.abs(diceSum(myDice) - diceSum(otherDice)) <= (params.diff ?? 0);
    }

    case 'hand_higher_sum_lower': {
      const ni = neighborIndex(playerIndex, playerCount, params.neighbor ?? 'right');
      const other = getHand(boards[ni]);
      const otherDice = getDice(boards[ni]);
      if (!other || !otherDice) return false;
      return compareHands(myHand, other) > 0 && diceSum(myDice) < diceSum(otherDice);
    }

    case 'hand_lower_sum_higher': {
      const ni = neighborIndex(playerIndex, playerCount, params.neighbor ?? 'right');
      const other = getHand(boards[ni]);
      const otherDice = getDice(boards[ni]);
      if (!other || !otherDice) return false;
      return compareHands(myHand, other) < 0 && diceSum(myDice) > diceSum(otherDice);
    }

    case 'compare_targets_hand_sum_cross': {
      const handHigher = getHandByTarget(ctx, params.handHigher ?? 'left');
      const handLower = getHandByTarget(ctx, params.handLower ?? 'right');
      const sumHigherDice = getDiceByTarget(ctx, params.sumHigher ?? 'right');
      const sumLowerDice = getDiceByTarget(ctx, params.sumLower ?? 'left');
      if (!handHigher || !handLower || !sumHigherDice || !sumLowerDice) return false;
      return (
        compareHands(handHigher, handLower) > 0 &&
        diceSum(sumHigherDice) > diceSum(sumLowerDice)
      );
    }

    case 'target_hand_extreme': {
      const targetIndex = resolveBoardIndex(playerIndex, playerCount, params.target ?? 'self');
      const targetHand = getHand(boards[targetIndex]);
      if (!targetHand) return false;
      const hands = allHands(boards);
      const extreme = params.extreme ?? 'highest';

      for (let i = 0; i < hands.length; i += 1) {
        if (i === targetIndex) continue;
        const comparison = compareHands(targetHand, hands[i]);
        if (extreme === 'highest' && comparison < 0) return false;
        if (extreme === 'lowest' && comparison > 0) return false;
      }

      return true;
    }

    case 'neighbors_same_combo': {
      const left = getHand(boards[leftOf(playerIndex, playerCount)]);
      const right = getHand(boards[rightOf(playerIndex, playerCount)]);
      if (!left || !right) return false;
      return hasSameComboName(left, right);
    }

    case 'neighbors_different_combo': {
      const left = getHand(boards[leftOf(playerIndex, playerCount)]);
      const right = getHand(boards[rightOf(playerIndex, playerCount)]);
      if (!left || !right) return false;
      return hasDifferentComboName(left, right);
    }

    case 'neighbors_sum_diff': {
      const leftDice = getDice(boards[leftOf(playerIndex, playerCount)]);
      const rightDice = getDice(boards[rightOf(playerIndex, playerCount)]);
      if (!leftDice || !rightDice) return false;
      const gap = Math.abs(diceSum(leftDice) - diceSum(rightDice));
      const diff = params.diff ?? 0;
      return params.mode === 'min' ? gap >= diff : gap <= diff;
    }

    case 'combo_asymmetric_neighbors': {
      const strong = getHandByTarget(ctx, params.strong ?? 'right');
      const weak = getHandByTarget(ctx, params.weak ?? 'left');
      if (!strong || !weak) return false;
      const minRank = params.minRank ?? 7;
      return strong.rank <= minRank && weak.rank === 8;
    }

    case 'last_die_in_combo': {
      const lastDie = myBoard[4];
      if (lastDie === null) return false;
      return isDiePartOfCombo(myDice, lastDie, myHand);
    }

    case 'last_die_is_max': {
      const lastDie = myBoard[4];
      if (lastDie === null) return false;
      return lastDie === Math.max(...myDice);
    }

    default:
      return false;
  }
}

export function evaluateMissionById(
  playerIndex: number,
  playerCount: number,
  boards: (number | null)[][],
  missionId: string,
): boolean {
  const mission = MISSION_BY_ID[missionId];
  if (!mission) return false;
  return evaluateMissionSuccess({ playerIndex, playerCount, boards, mission });
}

export function estimateMissionSuccessProbability(
  playerIndex: number,
  playerCount: number,
  boards: (number | null)[][],
  missionId: string,
): number {
  return evaluateMissionById(playerIndex, playerCount, boards, missionId) ? 1 : 0;
}

export { compareDiceHands };
